import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Box, Button, Collapse, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, Divider, Icon } from '@alycecom/ui';

import StepSection from '../StepSection/StepSection';
import { DeclineOptions } from '../../constants/unsubscribe.constants';
import { getSingleDeclineTitle } from '../../helpers/unsubscribe.helpers';
import UnsubscribeAllConfirmation from '../UnsubscribeAllConfirmation/UnsubscribeAllConfirmation';
import { IGift } from '../../types/gift';
import { ServerErrors } from '../../types/errors';

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing }) => ({
  labelTextMargin: {
    marginBottom: spacing(1),
  },
  warningIcon: {
    color: palette.red.main,
    marginRight: spacing(1),
  },
  MuiFormControlLabel: {
    alignItems: 'flex-start',
  },
  MuiRadioButton: {
    marginTop: -9,
  },
  submitButton: {
    width: 275,
    '@media (max-width:420px)': {
      width: '100%',
    },
  },
  textArea: {
    marginTop: spacing(2),
    marginLeft: spacing(4),
    width: 550,
    '@media (max-width:700px)': {
      width: '100%',
      marginLeft: 0,
    },
  },
}));

interface IDeclineGiftInviteStepProps {
  gift: IGift;
  isLoading: boolean;
  errors: ServerErrors;
  onSubmit: (declineType: string, feedback: string) => void;
  onChangeFeedback: () => void;
}

const DeclineGiftInviteStep = ({
  gift,
  errors,
  isLoading = false,
  onSubmit,
  onChangeFeedback,
}: IDeclineGiftInviteStepProps): ReactElement => {
  const classes = useStyles();
  const { sender, recipient, company, canBeDeclined } = gift;

  const [declineType, setDeclineType] = useState<DeclineOptions>(
    canBeDeclined ? DeclineOptions.One : DeclineOptions.All,
  );
  const [feedback, setFeedback] = useState('');
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);

  const isSingleDecline = declineType === DeclineOptions.One;
  const isPluralDecline = declineType === DeclineOptions.All;
  const isDisabled = isLoading || !declineType;
  const singleDeclineTitle = getSingleDeclineTitle(company, sender);

  const handleSubmit = useCallback(() => {
    onSubmit(declineType, feedback);
  }, [onSubmit, declineType, feedback]);

  const handleChangeFeedback = useCallback(
    event => {
      setFeedback(event.target.value);
      onChangeFeedback();
    },
    [setFeedback, onChangeFeedback],
  );

  const tryHandleSubmit = useCallback(() => {
    if (declineType === DeclineOptions.All) {
      setShowUnsubscribeConfirm(true);
    } else {
      handleSubmit();
    }
  }, [handleSubmit, declineType]);

  const submitConfirmation = useCallback(() => {
    setShowUnsubscribeConfirm(false);
    handleSubmit();
  }, [handleSubmit, setShowUnsubscribeConfirm]);

  const discardConfirmation = useCallback(() => {
    setShowUnsubscribeConfirm(false);
  }, [setShowUnsubscribeConfirm]);

  useEffect(() => {
    setFeedback('');
    onChangeFeedback();
  }, [onChangeFeedback]);

  return (
    <>
      <StepSection
        data-testid="declineGiftInviteStepSection"
        title="Step 1. Decline your Alyce gift invitation or unsubscribe from all future Alyce emails."
        iconProps={{ icon: 'gift', color: 'primary' }}
      >
        <Typography className="Body-Regular-Left-Static" data-testid="declineGiftInviteStepUserEmail">
          Your email address: {recipient.email}
        </Typography>
        {canBeDeclined && <Divider mt={4} mb={4} />}
        <RadioGroup
          aria-label="declineType"
          name="declineType"
          value={declineType}
          onChange={event => setDeclineType(event.target.value as DeclineOptions)}
        >
          {canBeDeclined && (
            <>
              <FormControlLabel
                data-testid="declineControlLabel"
                value={DeclineOptions.One}
                control={<Radio classes={{ root: classes.MuiRadioButton }} color="primary" />}
                classes={{ root: classes.MuiFormControlLabel }}
                label={
                  <Box>
                    <Typography
                      data-testid="declineControlLabel-labelText"
                      className={classNames([isSingleDecline ? 'H4-Dark-Bold' : 'H4-Light', classes.labelTextMargin])}
                    >
                      {singleDeclineTitle}
                    </Typography>
                    <Typography
                      data-testid="declineControlLabel-labelTip"
                      className={isSingleDecline ? 'Body-Regular-Left-Static' : 'Body-Regular-Left-Inactive'}
                    >
                      Decline this gift invitation you’ve currently received and there will be no further related email.
                    </Typography>
                  </Box>
                }
              />
              <Collapse in={isSingleDecline}>
                <TextField
                  data-testid="declineFeedbackField"
                  className={classes.textArea}
                  name="feedback"
                  label="Enter your feedback"
                  variant="outlined"
                  multiline
                  rows={5}
                  value={feedback}
                  error={!!(errors && errors.feedback)}
                  helperText={errors && errors.feedback}
                  disabled={isLoading}
                  onChange={handleChangeFeedback}
                />
              </Collapse>
            </>
          )}
          <Divider mt={4} mb={4} />
          <FormControlLabel
            data-testid="unsubscribeControlLabel"
            value={DeclineOptions.All}
            control={<Radio classes={{ root: classes.MuiRadioButton }} color="primary" />}
            classes={{ root: classes.MuiFormControlLabel }}
            label={
              <Box>
                <Typography
                  data-testid="unsubscribeControlLabel-labelText"
                  className={classNames([isPluralDecline ? 'H4-Dark-Bold' : 'H4-Light', classes.labelTextMargin])}
                >
                  Any future emails from Alyce
                </Typography>
                <Typography
                  data-testid="unsubscribeControlLabel-labelTip"
                  className={classNames([
                    isPluralDecline ? 'Body-Regular-Left-Static' : 'Body-Regular-Left-Inactive',
                    classes.labelTextMargin,
                  ])}
                >
                  You will automatically decline all your gift invitations and will not receive any future emails from
                  Alyce.
                </Typography>
                <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
                  <Icon className={classes.warningIcon} icon="exclamation-circle" />
                  <Typography data-testid="unsubscribeControlLabel-warningMessage" className="Subcopy-Error">
                    Your email address, listed above, will not receive any further Alyce gift invitation related emails
                    or updates
                  </Typography>
                </Box>
              </Box>
            }
          />
          <Collapse in={isPluralDecline}>
            <TextField
              data-testid="unsubscribeAllFeedbackField"
              className={classes.textArea}
              name="feedback"
              label="Enter your feedback"
              variant="outlined"
              multiline
              rows={5}
              value={feedback}
              error={!!(errors && errors.feedback)}
              helperText={errors && errors.feedback}
              disabled={isLoading}
              onChange={handleChangeFeedback}
            />
          </Collapse>
        </RadioGroup>
        <Divider mt={4} mb={4} />
        <Button
          data-testid="declineSubmit"
          className={classes.submitButton}
          variant="contained"
          color="secondary"
          onClick={tryHandleSubmit}
          disabled={isDisabled}
        >
          Submit unsubscribe options
        </Button>
      </StepSection>
      <UnsubscribeAllConfirmation
        isOpen={showUnsubscribeConfirm}
        onSubmit={submitConfirmation}
        onDiscard={discardConfirmation}
      />
    </>
  );
};

export default DeclineGiftInviteStep;
